import {
  getHeaderImage,
  BRANDTV_LOGO,
  getContrastingBrandliveLogo,
  FOOTER_CARD_STYLE,
  getSessionDateLabel,
  getSessionTimeLabel,
  resolveThemedColor,
  resolveThemedFont,
  resolveThemedRadius,
  formatDateParts,
} from './utils/utils';
import { ECustomDesignTypes, EPaletteModes } from '@/types/theme-packs.types';
import type { TemplateProps } from './campaign-template.types';
import ContentEditable from './content-editable';
import styles from './template-styles.module.css';
import HeaderWidget from './widgets/header-widget';
import ImageWidget from './widgets/image-widget';
import ParagraphWidget from './widgets/paragraph-widget';
import FooterWidget from './widgets/footer-widget';
import LogoWidget from './widgets/logo-widget';
import SocialWidget from './widgets/social-widget';
import { ICONS } from '@ui/icons/types';
import { templateStaticDefaults } from './template-defaults';
import { BlockRenderer } from './widgets/block-renderer';
import BlockShell from '../editor/components/block-shell';
import CalendarLinksWidget from './widgets/calendar-links-widget';
import { BLOCK_ID } from './widgets/block-ids';
import { DateTimeLayout } from './widgets/date-time-layout';

const DEFAULTS = templateStaticDefaults.florence;

const FLORENCE_BLOCK_PRESETS: Record<string, Record<string, unknown>> = {
  [BLOCK_ID.topBar]: { background: 'var(--accent)' },
  [BLOCK_ID.headerLogo]: { height: 24, align: 'center', padding: '32px 56px 8px 56px' },
  [BLOCK_ID.title]: {
    color: 'var(--heading)',
    fontFamily: 'var(--heading-font)',
    fontSize: '64px',
    fontWeight: 700,
    lineHeight: '64px',
    textTransform: 'uppercase',
    padding: '20px 56px',
  },
  [BLOCK_ID.mainImage]: {
    width: 488,
    height: 240,
    padding: '0 56px 12px 56px',
    borderRadius: 'var(--image-radius)',
    filter: 'grayscale(100%)',
  },
  [BLOCK_ID.cta]: {
    fullWidth: true,
    tdPadding: '24px',
    color: 'var(--button-text)',
    buttonColor: 'var(--button-color)',
    fontFamily: 'var(--body-font)',
    fontWeight: 600,
    letterSpacing: '0.36px',
    textTransform: 'uppercase',
    fontSize: '18px',
    padding: '20px 56px',
  },
  [BLOCK_ID.description]: {
    color: '#111111',
    fontFamily: 'var(--body-font)',
    fontSize: '12px',
    letterSpacing: '0.24px',
    textTransform: 'uppercase',
    lineHeight: '19px',
    padding: '32px 56px',
  },
  [BLOCK_ID.social]: { color: 'var(--heading)', padding: '8px 56px' },
  [BLOCK_ID.legalText]: {
    color: '#979797',
    fontFamily: 'var(--body-font)',
    fontSize: '12px',
    fontWeight: 400,
    letterSpacing: '0.24px',
    lineHeight: '19px',
    textAlign: 'center',
    opacity: 0.6,
    width: 314,
    padding: '20px 0 40px 0',
  },
  // the date & time block keeps florence's original outlined stacked card
  [BLOCK_ID.dateTimeCard]: {
    layout: DateTimeLayout.Stacked,
    padding: '12px 56px',
    border: '2px',
    borderStyle: 'solid',
    borderColor: 'var(--heading)',
    cardBorderRadius: '0',
    dateRowPadding: '24px',
    groupGap: '24px',
    dateHeading: '/ Date',
    timeHeading: '/ Time',
    labelStyle: {
      color: 'var(--heading)',
      fontSize: '12px',
      fontWeight: 700,
      letterSpacing: '0.24px',
      marginBottom: '4px',
    },
    valueStyle: {
      color: 'var(--heading)',
      fontSize: '24px',
      fontFamily: 'var(--heading-font)',
      textTransform: 'uppercase',
    },
  },
  [BLOCK_ID.mailingAddress]: { ...FOOTER_CARD_STYLE, fontFamily: 'var(--body-font)' },
};

export const Florence = ({
  event,
  theme,
  mode,
  timezone,
  href,
  calendarLinks,
  customizations,
  title,
  setTitle,
  description,
  setDescription,
  cta,
  setCta,
  setMainImage,
  setHeaderImage,
  blocks,
  onUpdateBlockValue,
  onUpdateBlockImage,
  onDeleteBlock,
  onMoveBlock,
  selectedBlockId,
  onSelectBlock,
  isEditMode,
  hideBrandliveLogo,
  privacyPolicyLabel,
}: TemplateProps) => {
  const resolvedMode = mode ?? theme?.theme_mode ?? EPaletteModes.Light;
  const themeColors = theme?.colors?.[resolvedMode] ?? theme?.colors;
  const bg = resolveThemedColor(
    customizations?.backgroundColor,
    themeColors?.backgroundColor,
    DEFAULTS.backgroundColor
  );
  const container = resolveThemedColor(
    customizations?.containerColor,
    themeColors?.containerColor,
    DEFAULTS.containerColor
  );
  const accent = resolveThemedColor(customizations?.accentColor, themeColors?.accentColor, DEFAULTS.accentColor);
  const heading = resolveThemedColor(
    customizations?.headingTextColor,
    themeColors?.headingTextColor,
    DEFAULTS.headingTextColor
  );
  const body = resolveThemedColor(customizations?.bodyTextColor, themeColors?.bodyTextColor, DEFAULTS.bodyTextColor);
  const buttonText = resolveThemedColor(
    customizations?.buttonTextColor,
    themeColors?.buttonTextColor,
    DEFAULTS.buttonTextColor ?? accent
  );
  const buttonColor = DEFAULTS.buttonColor ?? accent;
  const buttonRadius = resolveThemedRadius(theme?.custom_styles?.[ECustomDesignTypes.button], '0px');
  const containerRadius = resolveThemedRadius(theme?.custom_styles?.[ECustomDesignTypes.container], '0px');
  const imageRadius = resolveThemedRadius(theme?.custom_styles?.[ECustomDesignTypes.img], '0px');
  const sessionTimestamp = event.sessions[0]?.timestamp;
  const sessionDateLabel = getSessionDateLabel(sessionTimestamp, timezone, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
  const sessionTimeLabel = getSessionTimeLabel(sessionTimestamp, timezone, { hour: '2-digit', hour12: false });
  const sessionDateParts = sessionTimestamp ? formatDateParts(sessionTimestamp, timezone) : null;
  const anchorHref = href || '#';
  const headerImg = customizations?.headingImage || getHeaderImage(event) || BRANDTV_LOGO;
  const bodyImg = customizations?.mainImage || DEFAULTS.image;
  const headingFont = resolveThemedFont(
    customizations?.headingFont,
    theme?.font_pack?.heading_name,
    '"Spline Sans", Arial Black, sans-serif'
  );
  const bodyFont = resolveThemedFont(
    customizations?.bodyFont,
    theme?.font_pack?.body_name,
    '"Spline Sans", Arial, sans-serif'
  );
  const eventTitle = title ?? (customizations?.title || DEFAULTS.title);
  const eventDescription = description || customizations?.description || DEFAULTS.description;
  const _cta = cta ?? (customizations?.cta || DEFAULTS.cta);

  const bodyColor = container;
  const footerTextColor = '#111111';
  const footerBackground = bg;
  const brandliveLogo = hideBrandliveLogo ? undefined : getContrastingBrandliveLogo(bodyColor);

  if (blocks?.length) {
    const theme = {
      bg,
      container,
      accent,
      buttonColor,
      heading,
      buttonText,
      body,
      headingFont,
      bodyFont,
      imageRadius,
      buttonRadius,
      containerRadius,
      bodyBg: bodyColor,
      footerText: footerTextColor,
      footerBg: footerBackground,
    };
    const blockContext = {
      blockPresets: FLORENCE_BLOCK_PRESETS,
      brandliveLogo,
      privacyPolicyLabel,
      href: anchorHref,
      theme,
      dateLabel: timezone ? sessionDateLabel : null,
      timeLabel: timezone ? sessionTimeLabel : null,
      dateParts: timezone ? sessionDateParts : null,
    };

    return (
      <table
        data-component="Florence"
        width="100%"
        cellPadding={0}
        cellSpacing={0}
        border={0}
        role="presentation"
        className={styles.email}
        style={
          {
            '--bg': bg,
            '--container': container,
            '--accent': accent,
            '--button-color': buttonColor,
            '--heading': heading,
            '--body': body,
            '--button-text': buttonText,
            '--heading-font': headingFont,
            '--body-font': bodyFont,
            '--image-radius': imageRadius,
            '--button-radius': buttonRadius,
            background: 'var(--bg)',
          } as React.CSSProperties
        }
      >
        <tbody>
          <tr>
            <td align="center" style={{ padding: '0 32px' }}>
              <table
                width={600}
                cellPadding={0}
                cellSpacing={0}
                border={0}
                role="presentation"
                style={{ maxWidth: '600px' }}
              >
                <tbody>
                  <tr>
                    <td style={{ background: 'var(--container)' }}>
                      {blocks.map((block) => {
                        return (
                          <BlockShell
                            key={block.id}
                            block={block}
                            isEditMode={!!isEditMode}
                            isFirst={block.id === blocks[0]?.id}
                            isLast={block.id === blocks[blocks.length - 1]?.id}
                            selected={block.id === selectedBlockId}
                            onSelect={onSelectBlock}
                            onDelete={onDeleteBlock}
                            onMoveUp={(id) => onMoveBlock?.(id, 'up')}
                            onMoveDown={(id) => onMoveBlock?.(id, 'down')}
                          >
                            <BlockRenderer
                              block={block}
                              stylePreset={FLORENCE_BLOCK_PRESETS[block.id]}
                              onValueChange={onUpdateBlockValue}
                              onImageChange={onUpdateBlockImage}
                              context={blockContext}
                            />
                          </BlockShell>
                        );
                      })}
                    </td>
                  </tr>
                </tbody>
              </table>

              <table
                width={600}
                cellPadding={0}
                cellSpacing={0}
                border={0}
                role="presentation"
                style={{ maxWidth: '600px' }}
              >
                <tbody>
                  {!!calendarLinks && (
                    <tr>
                      <td align="center" style={{ padding: '24px 0' }}>
                        <CalendarLinksWidget links={calendarLinks} color="var(--body)" padding="0" />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>
    );
  }

  return (
    <table
      data-component="Florence"
      width="100%"
      cellPadding={0}
      cellSpacing={0}
      border={0}
      role="presentation"
      className={styles.email}
      style={
        {
          '--bg': bg,
          '--container': container,
          '--accent': accent,
          '--button-color': buttonColor,
          '--heading': heading,
          '--body': body,
          '--button-text': buttonText,
          '--heading-font': headingFont,
          '--body-font': bodyFont,
          background: 'var(--bg)',
        } as React.CSSProperties
      }
    >
      <tbody>
        <tr>
          <td align="center" style={{ padding: '0 32px' }}>
            <table
              width={600}
              cellPadding={0}
              cellSpacing={0}
              border={0}
              role="presentation"
              style={{ maxWidth: '600px', background: 'var(--container)' }}
            >
              <tbody>
                <tr>
                  <td height={20} style={{ background: 'var(--accent)', fontSize: 0, lineHeight: '0' }}>
                    &nbsp;
                  </td>
                </tr>

                <tr>
                  <td>
                    <LogoWidget
                      src={headerImg}
                      setImage={setHeaderImage}
                      height={24}
                      align="center"
                      padding="32px 56px 8px 56px"
                    />

                    <HeaderWidget
                      value={eventTitle}
                      setValue={setTitle}
                      color="var(--heading)"
                      fontFamily="var(--heading-font)"
                      fontSize="64px"
                      fontWeight={700}
                      lineHeight="64px"
                      textTransform="uppercase"
                      padding="20px 56px"
                    />

                    <ImageWidget
                      src={bodyImg}
                      setImage={setMainImage}
                      width={488}
                      height={240}
                      padding="0 56px 12px 56px"
                      borderRadius={imageRadius}
                      filter="grayscale(100%)"
                    />

                    {sessionTimestamp && (
                      <table role="presentation" width="100%" cellPadding={0} cellSpacing={0} border={0}>
                        <tbody>
                          <tr>
                            <td style={{ padding: '12px 56px' }}>
                              <table
                                width="100%"
                                cellPadding={0}
                                cellSpacing={0}
                                border={0}
                                role="presentation"
                                style={{ border: '2px solid var(--heading)' }}
                              >
                                <tbody>
                                  <tr>
                                    <td style={{ padding: '24px' }}>
                                      <div
                                        style={{
                                          color: 'var(--heading)',
                                          fontSize: '12px',
                                          fontFamily: 'var(--body-font)',
                                          fontWeight: 700,
                                          letterSpacing: '0.24px',
                                          textTransform: 'uppercase',
                                          marginBottom: '4px',
                                        }}
                                      >
                                        / Date
                                      </div>
                                      <div
                                        style={{
                                          color: 'var(--heading)',
                                          fontSize: '24px',
                                          fontFamily: 'var(--heading-font)',
                                          fontWeight: 700,
                                          textTransform: 'uppercase',
                                          marginBottom: '24px',
                                        }}
                                      >
                                        {sessionDateLabel}
                                      </div>
                                      <div
                                        style={{
                                          color: 'var(--heading)',
                                          fontSize: '12px',
                                          fontFamily: 'var(--body-font)',
                                          fontWeight: 700,
                                          letterSpacing: '0.24px',
                                          textTransform: 'uppercase',
                                          marginBottom: '4px',
                                        }}
                                      >
                                        / Time
                                      </div>
                                      <div
                                        style={{
                                          color: 'var(--heading)',
                                          fontSize: '24px',
                                          fontFamily: 'var(--heading-font)',
                                          fontWeight: 700,
                                          textTransform: 'uppercase',
                                        }}
                                      >
                                        {sessionTimeLabel}
                                      </div>
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    )}

                    <table role="presentation" width="100%" cellPadding={0} cellSpacing={0} border={0}>
                      <tbody>
                        <tr>
                          <td style={{ padding: '20px 56px' }}>
                            <table width="100%" cellPadding={0} cellSpacing={0} border={0} role="presentation">
                              <tbody>
                                <tr>
                                  <td
                                    align="center"
                                    valign="middle"
                                    style={{
                                      background: 'var(--button-color)',
                                      padding: '24px',
                                      borderRadius: buttonRadius,
                                    }}
                                  >
                                    <a
                                      href={anchorHref}
                                      onClick={(e) => e.preventDefault()}
                                      style={{ textDecoration: 'none', display: 'inline-block' }}
                                    >
                                      <ContentEditable
                                        value={_cta}
                                        setValue={setCta}
                                        style={{
                                          color: 'var(--button-text)',
                                          fontSize: '18px',
                                          fontFamily: 'var(--body-font)',
                                          fontWeight: 600,
                                          letterSpacing: '0.36px',
                                          textTransform: 'uppercase',
                                          wordWrap: 'break-word',
                                          whiteSpace: 'nowrap',
                                        }}
                                      />
                                    </a>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      </tbody>
                    </table>

                    <ParagraphWidget
                      value={eventDescription}
                      setValue={setDescription}
                      color="#111111"
                      fontFamily="var(--body-font)"
                      fontSize="12px"
                      letterSpacing="0.24px"
                      textTransform="uppercase"
                      lineHeight="19px"
                      padding="32px 56px"
                    />

                    <SocialWidget
                      links={[
                        { platform: ICONS.FACEBOOK, href: '#' },
                        { platform: ICONS.INSTAGRAM, href: '#' },
                        { platform: ICONS.TWITTER, href: '#' },
                      ]}
                      color="var(--heading)"
                      padding="8px 56px"
                    />

                    <table role="presentation" width="100%" cellPadding={0} cellSpacing={0} border={0}>
                      <tbody>
                        <tr>
                          <td align="center" style={{ padding: '20px 0 40px 0' }}>
                            <table width={314} cellPadding={0} cellSpacing={0} border={0} style={{ margin: '0 auto' }}>
                              <tbody>
                                <tr>
                                  <td>
                                    <p
                                      style={{
                                        margin: 0,
                                        color: '#979797',
                                        opacity: 0.6,
                                        fontSize: '12px',
                                        fontFamily: 'var(--body-font)',
                                        fontWeight: 400,
                                        letterSpacing: '0.24px',
                                        lineHeight: '19px',
                                        textAlign: 'center',
                                        wordWrap: 'break-word',
                                      }}
                                    >
                                      {DEFAULTS.complianceText}
                                    </p>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    <FooterWidget
                      value={DEFAULTS.footerText}
                      src=""
                      brandliveLogo={brandliveLogo}
                      privacyPolicyLabel={privacyPolicyLabel}
                      bodyColor={bodyColor}
                      borderRadius={containerRadius}
                      accentColor={accent}
                      color={footerTextColor}
                      background={footerBackground}
                      {...FOOTER_CARD_STYLE}
                      fontFamily="var(--body-font)"
                    />
                  </td>
                </tr>
              </tbody>
            </table>

            <table
              width={600}
              cellPadding={0}
              cellSpacing={0}
              border={0}
              role="presentation"
              style={{ maxWidth: '600px' }}
            >
              <tbody>
                {!!calendarLinks && (
                  <tr>
                    <td align="center" style={{ padding: '24px 0' }}>
                      <table cellPadding={0} cellSpacing={0} border={0} role="presentation">
                        <tbody>
                          <tr>
                            <td>
                              <a
                                style={{
                                  color: 'var(--body)',
                                  fontSize: '12px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 600,
                                  lineHeight: '20px',
                                  letterSpacing: '0.24px',
                                }}
                                href={calendarLinks.iCal}
                              >
                                iCal
                              </a>
                            </td>
                            <td
                              style={{
                                padding: '0 4px',
                                color: 'var(--body)',
                                fontSize: '11px',
                                fontFamily: "'TASA Orbiter', sans-serif",
                                fontWeight: 600,
                                lineHeight: '16px',
                                letterSpacing: '0.22px',
                              }}
                            >
                              •
                            </td>
                            <td>
                              <a
                                style={{
                                  color: 'var(--body)',
                                  fontSize: '12px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 600,
                                  lineHeight: '20px',
                                  letterSpacing: '0.24px',
                                }}
                                href={calendarLinks.outlook}
                              >
                                Outlook
                              </a>
                            </td>
                            <td
                              style={{
                                padding: '0 4px',
                                color: 'var(--body)',
                                fontSize: '11px',
                                fontFamily: "'TASA Orbiter', sans-serif",
                                fontWeight: 600,
                                lineHeight: '16px',
                                letterSpacing: '0.22px',
                              }}
                            >
                              •
                            </td>
                            <td>
                              <a
                                style={{
                                  color: 'var(--body)',
                                  fontSize: '12px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 600,
                                  lineHeight: '20px',
                                  letterSpacing: '0.24px',
                                }}
                                href={calendarLinks.google}
                              >
                                Google
                              </a>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  );
};
